import { AxiosResponse } from "axios";
import { Communication, CommunicationDetail } from "../../models/Communications";
import { HttpService } from "./HttpService";

export interface CommunicationsListResponse {
    items: Communication[];
    allItemsNumber: number;
    currentPage: number;
    totalPages: number;
}

export class CommunicationHttpService {
    private static readonly BASE = "/communications";
    private static readonly MANAGER_BASE = "/manager/communications";

    static list(
        page: number,
        pageSize: number,
        search?: string,
        category?: string,
    ): Promise<AxiosResponse<CommunicationsListResponse>> {
        const params: Record<string, unknown> = { page, pageSize };
        if (search) params.search = search;
        if (category) params.category = category;
        return HttpService.getLuxotticaAxios().get(this.BASE, { params });
    }

    static getById(id: number): Promise<AxiosResponse<CommunicationDetail>> {
        return HttpService.getLuxotticaAxios().get(`${this.BASE}/${id}`);
    }

    static create(data: {
        title: string;
        category: string;
        body: string;
    }): Promise<AxiosResponse<CommunicationDetail>> {
        return HttpService.getLuxotticaAxios().post(this.MANAGER_BASE, data);
    }

    static update(
        id: number,
        data: { title: string; category: string; body: string },
    ): Promise<AxiosResponse<CommunicationDetail>> {
        return HttpService.getLuxotticaAxios().put(`${this.MANAGER_BASE}/${id}`, data);
    }

    static delete(id: number): Promise<AxiosResponse<void>> {
        return HttpService.getLuxotticaAxios().delete(`${this.MANAGER_BASE}/${id}`);
    }

    /** Upload a file attachment to an existing communication. */
    static uploadAttachment(communicationId: number, file: File): Promise<AxiosResponse<void>> {
        const form = new FormData();
        form.append("file", file);
        return HttpService.getLuxotticaAxios().post(
            `${this.MANAGER_BASE}/${communicationId}/attachments`,
            form,
        );
    }

    /**
     * Upload an image to embed in the body text.
     * Returns { id: number } — embed as {{IMAGE:id}} in the body.
     */
    static uploadImage(file: File): Promise<AxiosResponse<{ id: number }>> {
        const form = new FormData();
        form.append("file", file);
        return HttpService.getLuxotticaAxios().post(`${this.MANAGER_BASE}/images`, form);
    }

    /**
     * Fetch an embedded image as a blob URL (authenticated).
     * Use the returned URL as the `src` of an <img> element.
     * Call URL.revokeObjectURL(url) when the component unmounts.
     */
    static async getImageBlobUrl(attachmentId: number): Promise<string> {
        const response = await HttpService.getLuxotticaAxios().get(
            `${this.BASE}/images/${attachmentId}`,
            { responseType: "blob" },
        );
        return URL.createObjectURL(response.data as Blob);
    }

    /** Delete a file attachment from an existing communication. */
    static deleteAttachment(communicationId: number, attachmentId: number): Promise<AxiosResponse<void>> {
        return HttpService.getLuxotticaAxios().delete(
            `${this.MANAGER_BASE}/${communicationId}/attachments/${attachmentId}`,
        );
    }

    /** Trigger a browser download for a file attachment. */
    static async downloadAttachment(
        communicationId: number,
        attachmentId: number,
        fileName: string,
    ): Promise<void> {
        const response = await HttpService.getLuxotticaAxios().get(
            `${this.BASE}/${communicationId}/attachments/${attachmentId}/download`,
            { responseType: "blob" },
        );
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
    }
}
